import React, { type ReactNode } from 'react';
import Link from '@atlaskit/link';
import { Flex, Text, xcss } from '@atlaskit/primitives';
import Spinner from '@atlaskit/spinner';
import type { UseFetchError } from '@atlassian/jira-insights-fetch/src/controllers/use-fetch/types.tsx';
import { useIntl } from '@atlassian/jira-intl';
import {
	FireScreenAnalytics,
	ContextualAnalyticsData,
	DRAWER,
} from '@atlassian/jira-product-analytics-bridge';
import { SidebarError } from '../sidebar-error/index.tsx';
import type { DataType, NumericStats } from '../types.tsx';
import messages from './messages.tsx';

type Props = {
	data: DataType[] | NumericStats | undefined;
	loading: boolean;
	error: UseFetchError | undefined;
	isEmpty: boolean;
	children: ReactNode;
};

export const SidebarDataHandler = ({ data, loading, error, isEmpty, children }: Props) => {
	const { formatMessage } = useIntl();

	if (error) {
		return (
			<SidebarError
				errorMessage={formatMessage(messages.error, {
					link: (chunks: ReactNode[]) => (
						<Link href="https://support.atlassian.com/contact" appearance="default">
							{chunks}
						</Link>
					),
				})}
			/>
		);
	}

	if (loading || !data) {
		return (
			<Flex direction="column" alignItems="center" xcss={containerStyles}>
				<Spinner size="xlarge" label={formatMessage(messages.spinnerLabel)} />
			</Flex>
		);
	}

	if (isEmpty) {
		return (
			<ContextualAnalyticsData
				sourceType={DRAWER}
				sourceName="jira.issueFieldStatistics.emptyState"
			>
				<Flex direction="column" alignItems="center" xcss={containerStyles}>
					<Text weight="bold" align="center">
						{formatMessage(messages.noData)}
					</Text>
				</Flex>
				<FireScreenAnalytics />
			</ContextualAnalyticsData>
		);
	}

	return children;
};

const containerStyles = xcss({
	paddingTop: 'space.800',
	paddingBottom: 'space.800',
});
